import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PencilIcon } from "@heroicons/react/24/outline";
import { PencilLineIcon } from "lucide-react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Card from "../../../components/ui/Card";
import HoverActionsMenu from "../../../components/HoverActionsMenu";
import ConfirmDialog from "../../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../../icons/OctagonAlertIcon";

interface Note {
  id: string;
  author: string;
  avatarUrl: string;
  timeLabel: string;
  title: string;
  titlePreview: string;
  description: string;
  descriptionPreview: string;
  icon?: React.ReactNode;
}

interface NotesSectionProps {
  notes?: Note[];
  onAddNote?: () => void;
  onEditNote?: (noteId: string) => void;
  onDeleteNote?: (noteId: string) => void;
  onCopyNote?: (noteId: string) => void;
  onShareNote?: (noteId: string) => void;
}

const NoteCard: React.FC<{
  note: Note;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({ note, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Strip html tags to get plain text for comparison
  const getPlainText = (html: string) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const fullPlainText = getPlainText(note.description);
  const isTruncated = note.descriptionPreview.length < fullPlainText.length;

  return (
    <Card className="group relative overflow-hidden w-full h-full min-h-[120px]">
      {/* Hover Actions Menu */}
      <HoverActionsMenu
        onOpen={onEdit}
        onDelete={onDelete}
        className="!top-2 !right-2 !translate-y-0"
      />

      {/* Card Body */}
      <div className="flex flex-col gap-5 p-2">
        {/* Meta Section */}
        <div className="flex items-start gap-1 w-full">
          <div className="flex gap-1 items-center flex-1">
            {/* User Info */}
            <div className="flex gap-1 items-center">
              <img
                src={note.avatarUrl}
                alt={note.author}
                className="w-5 h-5 rounded-full flex-shrink-0"
              />
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{note.author}</p>
            </div>
            <p className="text-xs text-gray-600 whitespace-nowrap">{note.timeLabel}</p>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-2 w-full">
          {/* Title */}
          <div className="flex gap-1 items-start w-full" title={note.title}>
            <PencilLineIcon className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-semibold text-gray-900 flex-1">{note.titlePreview}</p>
          </div>

          {/* Description with Show more */}
          <div className="text-xs text-gray-600 text-wrap">
            {expanded ? (
              <span dangerouslySetInnerHTML={{ __html: note.description }} />
            ) : (
              <span>{note.descriptionPreview}</span>
            )}
            {isTruncated && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
                className="ml-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {expanded ? t("Show less") : t("Show more")}
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default function NotesSection({
  notes = [],
  onAddNote,
  onEditNote,
  onDeleteNote,
  onCopyNote: _onCopyNote,
  onShareNote: _onShareNote,
}: NotesSectionProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (noteId: string) => {
    setDeleteId(noteId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId || !onDeleteNote) return;
    try {
      setDeleting(true);
      await onDeleteNote(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
    } catch (error) {
      // Error handling can be added here if needed
      console.error("Failed to delete note:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="w-full space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between pb-2">
          <h2 className="text-lg font-semibold text-gray-900">{t("Notes")}</h2>
          <button
            onClick={onAddNote}
            className="inline-flex items-center gap-1.5 px-1.5 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 transition-colors"
          >
            <PlusIcon className="w-3 h-3 text-gray-900" />
            <span className="text-xs font-medium text-gray-900 whitespace-nowrap">
              {t("Add Note")}
            </span>
          </button>
        </div>

        {/* Notes Grid */}
        {notes.length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3 w-full">
            {notes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onEdit={() => onEditNote?.(note.id)}
                onDelete={() => handleDeleteClick(note.id)}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {notes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <PencilIcon className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">{t("No notes yet")}</p>
            <button
              onClick={onAddNote}
              className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              {t("Add your first note")}
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        visible={confirmOpen}
        title={t("Delete note?")}
        description={t("This action cannot be undone. The note will be permanently deleted.")}
        alertIcon={<OctagonAlertIcon />}
        dangerBtn
        submitting={deleting}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
            setDeleteId(null);
          }
        }}
        onSubmit={handleConfirmDelete}
        submitBtnLabel={t("Delete")}
      />
    </>
  );
}
